import React, { useState, useImperativeHandle, useEffect, forwardRef } from 'react'
import Api from '@/_helpers/Api'
import { trans } from '@/_providers/Translation'
import Ckeditor from '@/components/partials/Ckeditor'
import Dropdown from '@/components/partials/Dropdown'
import Signature from './Signature'
import { ENDPOINTS, CONTRACTORS_LIST, signatureUrl } from './config'

//images
import popupDefaultHeadImage from '@/assets/img/modals/icons/head.svg'
import loader from '@/assets/img/loader.svg'

const emptyData = {
    title: '',
    content: '',
    contractor: null,
}

// Лентата на редактора само за договорите. Подравняването работи по абзаци
// (виж paragraphs при Ckeditor), затова заглавието може да е центрирано, а
// останалият текст - ляво подравнен.
// TODO: 'fontSize' се добавя тук, щом сглобката на CKEditor в
// public/js/ckeditor получи плъгина - в текущата той липсва.
const CONTRACT_TOOLBAR = [
    'bold',
    'italic',
    'underline',
    'strikethrough',
    '|',
    'alignment',
]

function AddOrEdit(props, ref) {

    const contractType = props.typeId

    const [popup, setPopup] = useState({
        overlay: false,
        modal: false,
        edit: false,
        editId: null,
        loading: false,
        loadingData: false,
        data: { ...emptyData },
    })

    const [validations, setValidations] = useState(null)

    // Без подпис на управителя не може да се генерира документ, затова
    // формата се показва едва след като има качен подпис.
    const [signature, setSignature] = useState({
        loading: false,
        image: '',
    })

    const loadSignature = () => {
        setSignature(prev => ({
            ...prev,
            loading: true
        }))

        Api.get(ENDPOINTS.defaultSignature)
            .then(res => {
                setSignature({
                    loading: false,
                    image: signatureUrl(res.data),
                })
            }).catch(() => {
                // липсващ подпис не е грешка - показваме полето за качване
                setSignature({
                    loading: false,
                    image: '',
                })
            })
    }

    useImperativeHandle(ref, () => ({
        add: () => {
            setPopup(prev => ({
                ...prev,
                edit: false,
                editId: null,
                loading: false,
                loadingData: false,
                data: { ...emptyData, contractor: props.contractor || null },
            }))
            loadSignature()
            handleShow()
        },
        edit: id => {
            setPopup(prev => ({
                ...prev,
                edit: Math.random().toString(),
                editId: id,
                loading: false,
                data: { ...emptyData },
            }))
            loadSignature()
            handleShow()
        },
        hide: () => {
            handleHide()
        }
    }))

    // при редакция извлича текущите данни, за да се попълнят полетата
    useEffect(() => {
        if (popup.edit) {

            setPopup(prev => ({
                ...prev,
                loadingData: true
            }))

            Api.get(ENDPOINTS.find, {
                params: {
                    id: popup.editId,
                    contract_type: contractType,
                }
            }).then(res => {
                setPopup(prev => ({
                    ...prev,
                    data: {
                        ...emptyData,
                        ...res.data,
                    },
                }))
            }).finally(() => {
                setPopup(prev => ({
                    ...prev,
                    loadingData: false
                }))
            })
        }
    }, [popup.edit])

    const handleShow = () => {
        setPopup(prev => ({
            ...prev,
            overlay: true
        }))

        setTimeout(() => {
            setPopup(prev => ({
                ...prev,
                overlay: true,
                modal: true
            }))
        }, 50)
    }

    const handleHide = e => {
        if (e) {
            e.preventDefault()
        }

        setPopup(prev => ({
            ...prev,
            modal: false,
            loading: false,
        }))

        setTimeout(() => {
            setPopup(prev => ({
                ...prev,
                overlay: false,
                modal: false
            }))
        }, 50)

        setValidations(null)
    }

    const handleInputChange = e => {
        const { name, value } = e.target

        setPopup(prev => ({
            ...prev,
            data: {
                ...prev.data,
                [name]: value
            }
        }))
    }

    const handleSignatureSaved = image => {
        setSignature({
            loading: false,
            image: image,
        })
    }

    const handleContractorChange = data => {
        setPopup(prev => ({
            ...prev,
            data: {
                ...prev.data,
                contractor: data
            }
        }))
    }

    const handleSave = e => {
        e.preventDefault()

        setPopup(prev => ({
            ...prev,
            loading: true,
        }))

        setValidations(null)

        let url = popup.edit ? ENDPOINTS.edit : ENDPOINTS.add

        let data = new FormData(e.target)

        data.append('contract_type', contractType)

        if (popup.edit) {
            data.append('id', popup.editId)
        }

        Api.post(url, data)
            .then(res => {

                let contract = res.data || {}

                if (popup.edit) {
                    props.handleUpdate(contract)
                } else {
                    props.handleCreate(contract)
                }

                handleHide()

            }).catch(error => {

                setPopup(prev => ({
                    ...prev,
                    loading: false,
                }))

                let _err = error.response

                if (_err && _err.status && _err.status == 422) {
                    for (let [input, err] of Object.entries(_err.data.errors)) {

                        setValidations(prevState => {
                            return { ...prevState, [input]: true }
                        })
                    }
                }

            })
    }

    const hasSignature = Boolean(signature.image)

    return (
        <div className={`${popup.overlay ? 'visible' : ''} overlay`}>
            <form className={`${popup.modal ? 'show' : ''} popup-primary large contracts-modal`} onSubmit={handleSave}>
                <div className="head-new">
                    <img src={popupDefaultHeadImage} alt="icon" />
                    <div className="texts">
                        <h2>
                            {popup.edit ? trans('headings.editContract') : trans('headings.addContract')}
                        </h2>
                        <p>
                            {popup.edit ? trans('subheadings.editContract') : trans('subheadings.addContract')}
                        </p>
                    </div>
                    <span className="close" onClick={handleHide}>

                    </span>
                </div>

                {signature.loading &&
                    <div className="popup-container">
                        <div className="col signature-loader">
                            <img src={loader} alt="loading" />
                        </div>
                    </div>
                }

                {!signature.loading && !hasSignature &&
                    <>
                        <div className="popup-container">
                            <div className="col">
                                <p className="signature-required">
                                    {trans('random.signatureRequired')}
                                </p>
                            </div>
                        </div>

                        <br />

                        <div className="popup-container">
                            <div className="col">
                                <label>
                                    {trans('labels.signature')}
                                </label>
                                <Signature
                                    image=""
                                    onSaved={handleSignatureSaved}
                                />
                            </div>
                        </div>
                    </>
                }

                {!signature.loading && hasSignature &&
                    <>
                        <div className="popup-container x2">
                            <div className="col">
                                <label>
                                    {trans('labels.title')}
                                </label>
                                <input
                                    type="text"
                                    name="title"
                                    value={popup.data?.title || ''}
                                    onChange={handleInputChange}
                                    className={validations && validations.title ? 'validate' : ''}
                                />
                            </div>
                            <div className="col">
                                <label>
                                    {trans('labels.contractTo')}
                                </label>
                                <Dropdown
                                    inputName=""
                                    inputValue={popup.data?.contractor?.name || popup.data?.contractor_name || ''}
                                    inputIdName="contractor_id"
                                    inputIdValue={popup.data?.contractor?.id || popup.data?.contractor_id || ''}
                                    url={CONTRACTORS_LIST}
                                    params={{
                                        type: contractType,
                                        id: popup.data?.contractor?.id || null,
                                        limit: 100
                                    }}
                                    inputClassName={validations && validations.contractor_id ? 'validate' : ''}
                                    onChange={handleContractorChange}
                                    inputDisabled={Boolean(props.contractorId)}
                                />
                            </div>
                        </div>

                        <br />

                        <div className="popup-container">
                            <div className="col">
                                <label>
                                    {trans('labels.content')}
                                </label>
                                <div className={`contracts-editor ${validations && validations.content ? 'validate' : ''}`}>
                                    <Ckeditor
                                        adkey={`contract-content-${popup.editId || 'new'}`}
                                        name="content"
                                        value={popup.data?.content || ''}
                                        paragraphs
                                        toolbar={CONTRACT_TOOLBAR}
                                    />
                                </div>
                            </div>
                        </div>

                        <br />

                        <div className="popup-container">
                            <div className="col">
                                <label>
                                    {trans('labels.signature')}
                                </label>
                                <Signature
                                    image={signature.image}
                                    onSaved={handleSignatureSaved}
                                />
                            </div>
                        </div>
                    </>
                }

                <div className="footer align-right">
                    {popup.loading || popup.loadingData ?
                        <img src={loader} alt="loading" />
                        :
                        <>
                            <button className="button" onClick={handleHide}>
                                {trans('buttons.cancel')}
                            </button>
                            {hasSignature &&
                                <button className="button button-primary" type="submit">
                                    {trans('buttons.save')}
                                </button>
                            }
                        </>
                    }
                </div>
            </form>
        </div>
    )
}

export default forwardRef(AddOrEdit)
